import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { AuditLogDetail } from "@/hooks/use-audit-logs";

interface AuditLogDiffModalProps {
  log: AuditLogDetail | null;
  isOpen: boolean;
  onClose: () => void;
}

export const AuditLogDiffModal: React.FC<AuditLogDiffModalProps> = ({
  log,
  isOpen,
  onClose,
}) => {
  if (!log) return null;

  const formatDateTime = (dateStr: string | null | undefined): string => {
    if (!dateStr) return "Invalid date";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "Invalid date";
    return format(date, "PPpp");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {log.entity_name || "Unknown"} · {log.action}
          </DialogTitle>
          <DialogDescription className="text-sm space-y-1">
            <div>
              {log.entity_type || "N/A"} #{log.entity_id?.slice(0, 8) || "N/A"}
            </div>
            <div>
              {formatDateTime(log.occurred_at)} UTC
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline">{log.action}</Badge>
              {log.risk_level && (
                <Badge
                  variant={
                    log.risk_level === "high" || log.risk_level === "critical"
                      ? "destructive"
                      : log.risk_level === "medium"
                      ? "default"
                      : "secondary"
                  }
                >
                  {log.risk_level}
                </Badge>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Actor:</span>
              <div className="font-mono">{log.actor_email}</div>
            </div>
            {log.ip && (
              <div>
                <span className="text-muted-foreground">IP Address:</span>
                <div className="font-mono">{log.ip}</div>
              </div>
            )}
            <div>
              <span className="text-muted-foreground">Client:</span>
              <div className="capitalize">{log.client}</div>
            </div>
            {log.organization_id && (
              <div>
                <span className="text-muted-foreground">Organization:</span>
                <div className="font-medium">
                  {log.organization_name || log.organization_id}
                </div>
                {log.organization_name && (
                  <div className="text-xs text-muted-foreground font-mono">
                    ID: {log.organization_id}
                  </div>
                )}
              </div>
            )}
          </div>

          {log.diff && log.diff.length > 0 && (
            <div className="border rounded-lg p-4 bg-muted/20">
              <h3 className="font-semibold mb-3">Changes</h3>
              <div className="space-y-3">
                {log.diff.map((change, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-[120px_1fr_auto_1fr] gap-3 items-start text-sm"
                  >
                    <div className="font-medium text-muted-foreground">
                      {change.field}:
                    </div>
                    <div className="font-mono text-xs bg-destructive/10 text-destructive px-2 py-1 rounded break-all">
                      {change.old}
                    </div>
                    <div className="text-muted-foreground">→</div>
                    <div className="font-mono text-xs bg-green-500/10 text-green-600 dark:text-green-400 px-2 py-1 rounded break-all">
                      {change.new}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(!log.diff || log.diff.length === 0) && (
            <div className="text-sm text-muted-foreground text-center py-4">
              No detailed changes available for this log entry.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
